import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Image } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import LogoCarousel from '../components/LogoCarousel';
import HeroMockupCarousel from '../components/HeroMockupCarousel';
import { getPartnerLogos, getCarouselSettings } from '../services/firestoreService';

export default function HomeScreen({ onGetStarted, onSignIn }) {
  const { colors, isDark, themeMode, setTheme, gradients, glass } = useTheme();
  const [hoveredButton, setHoveredButton] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [partnerLogos, setPartnerLogos] = useState([]);
  const [carouselSpeed, setCarouselSpeed] = useState(40);
  const [logoSize, setLogoSize] = useState(120);
  const [logoSpacing, setLogoSpacing] = useState(60);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallDesktop, setIsSmallDesktop] = useState(false);
  const [isLargeDesktop, setIsLargeDesktop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const scrollViewRef = useRef(null);

  // Detect screen size breakpoints
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024); // Mobile & Tablet (< 1024px)
      setIsSmallDesktop(width >= 1024 && width < 1440); // Small Desktop
      setIsLargeDesktop(width >= 1440); // Large Desktop
      
      // Close mobile menu on desktop
      if (width >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Fake navigation object for Header
  const navigation = {
    navigate: (screen) => {
      if (screen === 'dashboard') return;
      // If trying to navigate elsewhere, trigger sign in
      onSignIn();
    }
  };

  const handleScroll = (event) => {
    const scrollY = event.nativeEvent.contentOffset.y;
    
    // Show/hide based on scroll direction
    if (scrollY > lastScrollY && scrollY > 100) {
      // Scrolling down & past threshold
      setIsVisible(false);
    } else {
      // Scrolling up
      setIsVisible(true);
    }
    
    // Change style if scrolled past threshold
    setIsScrolled(scrollY > 50);
    setLastScrollY(scrollY);
  };

  const toggleTheme = () => {
    // Toggle between light and dark (not system)
    setTheme(isDark ? 'light' : 'dark');
  };

  useEffect(() => {
    loadPartnerLogos();
    document.title = 'Flomodo - AI Assistant';
  }, []);

  const loadPartnerLogos = async () => {
    try {
      const [logos, settings] = await Promise.all([
        getPartnerLogos(),
        getCarouselSettings()
      ]);
      console.log('🎨 Loaded partner logos:', logos);
      console.log('⚙️ Carousel settings:', settings);
      setPartnerLogos(logos);
      setCarouselSpeed(settings.speed || 40);
      setLogoSize(settings.logoSize || 120);
      setLogoSpacing(settings.logoSpacing || 60);
    } catch (error) {
      console.error('Error loading partner logos:', error);
    }
  };

  return (
    <View style={[styles.wrapper, { backgroundImage: gradients.background }]}>
      {/* Smart Header with scroll behavior */}
      <View style={[
        styles.headerContainer,
        isMobile && styles.headerContainerMobileLanding,
        isScrolled && styles.headerScrolled,
        isScrolled && !isMobile && { 
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.7)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: isDark 
            ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
            : '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
        isMobile && {
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        },
        !isScrolled && !isMobile && { backgroundColor: 'transparent' },
        !isVisible && !isMobile && styles.headerHidden,
      ]}>
        <View style={[styles.header, isMobile && styles.headerMobileLanding]}>
          <TouchableOpacity style={[styles.logoButton, isMobile && styles.logoButtonMobileLanding]}>
            <Image 
              source={require('../assets/logo.png')} 
              style={[styles.logo, isMobile && styles.logoMobileLanding]}
              resizeMode="contain"
            />
          </TouchableOpacity>
          
          {isMobile ? (
            <TouchableOpacity 
              style={[styles.hamburgerLanding, { borderColor: colors.border }]}
              onPress={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <View style={[styles.hamburgerLineLanding, { backgroundColor: colors.text }]} />
              <View style={[styles.hamburgerLineLanding, { backgroundColor: colors.text }]} />
              <View style={[styles.hamburgerLineLanding, { backgroundColor: colors.text }]} />
            </TouchableOpacity>
          ) : (
            <View style={styles.headerRight}>
              <TouchableOpacity 
                style={[
                  styles.themeToggle,
                  { backgroundColor: colors.secondary, borderColor: colors.border }
                ]}
                onPress={toggleTheme}
              >
                <Text style={[styles.themeToggleText, { color: colors.text }]}>
                  {isDark ? '☀️' : '🌙'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[
                  styles.signInButton,
                  { backgroundColor: colors.secondary, borderColor: colors.border },
                  hoveredButton === 'headerSignIn' && { backgroundColor: colors.accent, borderColor: colors.accent }
                ]}
                onPress={onSignIn}
                onMouseEnter={() => setHoveredButton('headerSignIn')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <Text style={[
                  styles.signInButtonText, 
                  { color: hoveredButton === 'headerSignIn' ? '#fff' : colors.text }
                ]}>
                  Sign In
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>

      {/* Mobile Menu Dropdown */}
      {isMobile && mobileMenuOpen && (
        <View style={[
          styles.mobileMenuLanding,
          { 
            backgroundColor: isDark ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            borderColor: colors.border,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }
        ]}>
          <TouchableOpacity 
            style={[styles.mobileMenuItemLanding, { borderBottomColor: colors.border }]}
            onPress={toggleTheme}
          >
            <Text style={[styles.mobileMenuItemTextLanding, { color: colors.text }]}>
              {isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.mobileMenuItemLanding]}
            onPress={() => {
              setMobileMenuOpen(false);
              onSignIn();
            }}
          >
            <Text style={[styles.mobileMenuItemTextLanding, { color: colors.accent, fontWeight: '600' }]}>
              Sign In
            </Text>
          </TouchableOpacity>
        </View>
      )}

      <ScrollView 
        ref={scrollViewRef}
        style={styles.container} 
        contentContainerStyle={styles.contentContainer}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {/* Hero Section */}
        <View style={[
          styles.heroSection, 
          isMobile && styles.heroSectionMobile,
          isSmallDesktop && styles.heroSectionSmall,
          isLargeDesktop && styles.heroSectionLarge
        ]}>
        <View style={[
          styles.heroContent, 
          isMobile && styles.heroContentMobile,
          isSmallDesktop && styles.heroContentSmall
        ]}>
          <View style={[
            styles.heroTextContainer, 
            isMobile && styles.heroTextContainerMobile
          ]}>
            <Text style={[
              styles.heroTitle, 
              isMobile && styles.heroTitleMobile,
              isSmallDesktop && styles.heroTitleSmall,
              isLargeDesktop && styles.heroTitleLarge,
              { color: colors.text }
            ]}>
              Transform Your Workflow with{' '}
              <Text style={styles.heroTitleGradient}>AI-Powered</Text> Precision
            </Text>
            <Text style={[styles.heroSubtitle, isMobile && styles.heroSubtitleMobile, { color: colors.textSecondary }]}>
              Generate professional proposals and organized task lists in seconds. 
              Streamline your process with intelligent automation.
            </Text>
            
            <View style={[styles.heroButtons, isMobile && styles.heroButtonsMobile]}>
              <TouchableOpacity 
                style={[
                  styles.primaryButton,
                  isMobile && styles.primaryButtonMobile,
                  hoveredButton === 'primary' && styles.primaryButtonHover
                ]}
                onPress={onGetStarted}
                onMouseEnter={() => setHoveredButton('primary')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <Text style={[styles.primaryButtonText, isMobile && styles.primaryButtonTextMobile]}>Get Started Free</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[
                  styles.secondaryButton,
                  isMobile && styles.secondaryButtonMobile,
                  { backgroundColor: colors.card, borderColor: colors.border },
                  hoveredButton === 'secondary' && styles.secondaryButtonHover
                ]}
                onPress={onSignIn}
                onMouseEnter={() => setHoveredButton('secondary')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <Text style={[styles.secondaryButtonText, isMobile && styles.secondaryButtonTextMobile, { color: colors.accent }]}>Sign In</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Hero Image Container with Rotating Feature Examples */}
          <View style={[styles.heroImageContainer, isMobile && styles.heroImageContainerMobile]}>
            <View style={[styles.glassCard, isMobile && styles.glassCardMobile, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
              <HeroMockupCarousel />
            </View>
          </View>
        </View>
      </View>

      {/* Partner Logos Carousel */}
      <LogoCarousel logos={partnerLogos} speed={carouselSpeed} logoSize={logoSize} logoSpacing={logoSpacing} />

      {/* Features Section */}
      <View style={[styles.featuresSection, isMobile && styles.featuresSectionMobile]}>
        <Text style={[styles.sectionTitle, isMobile && styles.sectionTitleMobile, { color: colors.text }]}>Everything you need to stay productive</Text>
        
        <View style={[styles.featuresGrid, isMobile && styles.featuresGridMobile]}>
          <View style={[styles.featureCard, isMobile && styles.featureCardMobile, { ...glass, borderColor: colors.border }]}>
            <View style={[styles.featureIcon, isMobile && styles.featureIconMobile, styles.featureIcon1]}>
              <Text style={[styles.featureIconText, isMobile && styles.featureIconTextMobile]}>✨</Text>
            </View>
            <Text style={[styles.featureTitle, isMobile && styles.featureTitleMobile, { color: colors.text }]}>AI Proposals</Text>
            <Text style={[styles.featureDescription, isMobile && styles.featureDescriptionMobile, { color: colors.textSecondary }]}>
              Generate professional, customized proposals with intelligent context and examples
            </Text>
          </View>

          <View style={[styles.featureCard, isMobile && styles.featureCardMobile, { ...glass, borderColor: colors.border }]}>
            <View style={[styles.featureIcon, isMobile && styles.featureIconMobile, styles.featureIcon2]}>
              <Text style={[styles.featureIconText, isMobile && styles.featureIconTextMobile]}>📋</Text>
            </View>
            <Text style={[styles.featureTitle, isMobile && styles.featureTitleMobile, { color: colors.text }]}>Smart Task Lists</Text>
            <Text style={[styles.featureDescription, isMobile && styles.featureDescriptionMobile, { color: colors.textSecondary }]}>
              Transform conversations into structured, prioritized task lists instantly
            </Text>
          </View>

          <View style={[styles.featureCard, isMobile && styles.featureCardMobile, { ...glass, borderColor: colors.border }]}>
            <View style={[styles.featureIcon, isMobile && styles.featureIconMobile, styles.featureIcon3]}>
              <Text style={[styles.featureIconText, isMobile && styles.featureIconTextMobile]}>🎯</Text>
            </View>
            <Text style={[styles.featureTitle, isMobile && styles.featureTitleMobile, { color: colors.text }]}>Real-time Streaming</Text>
            <Text style={[styles.featureDescription, isMobile && styles.featureDescriptionMobile, { color: colors.textSecondary }]}>
              Watch your content generate in real-time with instant feedback
            </Text>
          </View>
        </View>
      </View>

      {/* CTA Section */}
      <View style={[styles.ctaSection, isMobile && styles.ctaSectionMobile]}>
        <View style={[styles.ctaCard, isMobile && styles.ctaCardMobile]}>
          <Text style={[styles.ctaTitle, isMobile && styles.ctaTitleMobile, { color: colors.text }]}>Ready to boost your productivity?</Text>
          <Text style={[styles.ctaSubtitle, isMobile && styles.ctaSubtitleMobile, { color: colors.textSecondary }]}>Join and start creating amazing content in minutes</Text>
          <TouchableOpacity 
            style={[
              styles.ctaButton,
              isMobile && styles.ctaButtonMobile,
              { backgroundColor: colors.accent },
              hoveredButton === 'cta' && styles.ctaButtonHover
            ]}
            onPress={onGetStarted}
            onMouseEnter={() => setHoveredButton('cta')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <Text style={[styles.ctaButtonText, isMobile && styles.ctaButtonTextMobile, { color: '#ffffff' }]}>Get Started Now</Text>
          </TouchableOpacity>
        </View>
      </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  
  // Header Styles
  headerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 2000,
    backgroundColor: 'transparent',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  headerScrolled: {
    top: 16,
    left: 16,
    right: 16,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 20,
  },
  headerHidden: {
    transform: 'translateY(-120%)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 12,
    position: 'relative',
  },
  logoButton: {
    padding: 4,
    zIndex: 2001,
  },
  logo: {
    width: 240,
    height: 68,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    zIndex: 2001,
  },
  signInButton: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 1,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  signInButtonHover: {
    transform: 'translateY(-1px)',
  },
  signInButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  themeToggle: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    marginRight: 12,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  themeToggleText: {
    fontSize: 18,
  },
  
  container: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  
  // Hero Section
  heroSection: {
    minHeight: '100vh',
    padding: 80,
    paddingTop: 120,
  },
  heroContent: {
    maxWidth: 1400,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 60,
    flexWrap: 'wrap',
  },
  heroTextContainer: {
    flex: 1,
    minWidth: 450,
  },
  heroTitle: {
    fontSize: 64,
    fontWeight: '800',
    lineHeight: 76,
    marginBottom: 28,
  },
  heroTitleGradient: {
    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent',
  },
  heroSubtitle: {
    fontSize: 22,
    lineHeight: 34,
    marginBottom: 40,
  },
  heroButtons: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  primaryButton: {
    paddingVertical: 18,
    paddingHorizontal: 40,
    borderRadius: 16,
    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.3s ease',
  },
  primaryButtonHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 40px rgba(102, 126, 234, 0.4)',
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '600',
  },
  secondaryButton: {
    paddingVertical: 18,
    paddingHorizontal: 40,
    borderRadius: 16,
    borderWidth: 2,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  secondaryButtonHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.2)',
  },
  secondaryButtonText: {
    fontSize: 17,
    fontWeight: '600',
  },
  
  // Hero Image
  heroImageContainer: {
    flex: 1,
    minWidth: 500,
    minHeight: 550,
    position: 'relative',
  },
  glassCard: {
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(20px)',
    borderRadius: 24,
    padding: 36,
    borderWidth: 1,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
  },
  mockupContent: {
    flex: 1,
    borderRadius: 16,
    overflow: 'hidden',
  },
  mockupHeader: {
    flexDirection: 'row',
    gap: 8,
    padding: 16,
    alignItems: 'center',
  },
  mockupDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  mockupTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  mockupBody: {
    padding: 24,
    gap: 12,
  },
  proposalPreview: {
    gap: 8,
  },
  proposalHeading: {
    fontSize: 16,
    fontWeight: '700',
    marginTop: 12,
    marginBottom: 8,
  },
  proposalLine: {
    height: 8,
    borderRadius: 4,
    marginBottom: 6,
    opacity: 0.6,
  },
  proposalLineShort: {
    width: '70%',
  },
  proposalLineMedium: {
    width: '85%',
  },
  
  // Features Section
  featuresSection: {
    padding: 100,
    paddingTop: 80,
    paddingBottom: 80,
  },
  sectionTitle: {
    fontSize: 48,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 64,
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
    maxWidth: 1400,
    marginHorizontal: 'auto',
    justifyContent: 'center',
  },
  featureCard: {
    flex: 1,
    minWidth: 300,
    maxWidth: 380,
    padding: 36,
    borderRadius: 24,
    borderWidth: 1,
    backdropFilter: 'blur(10px)',
  },
  featureIcon: {
    width: 68,
    height: 68,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  featureIcon1: {
    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  featureIcon2: {
    backgroundImage: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  featureIcon3: {
    backgroundImage: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  },
  featureIconText: {
    fontSize: 32,
  },
  featureTitle: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 14,
  },
  featureDescription: {
    fontSize: 17,
    lineHeight: 26,
  },
  
  // CTA Section
  ctaSection: {
    padding: 100,
    paddingTop: 80,
    paddingBottom: 80,
  },
  ctaCard: {
    maxWidth: 900,
    marginHorizontal: 'auto',
    alignItems: 'center',
  },
  ctaTitle: {
    fontSize: 48,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 24,
  },
  ctaSubtitle: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 36,
  },
  ctaButton: {
    paddingVertical: 20,
    paddingHorizontal: 48,
    borderRadius: 16,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  ctaButtonHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)',
  },
  ctaButtonText: {
    fontSize: 18,
    fontWeight: '600',
  },

  // Mobile & Tablet Responsive Styles (< 1024px)
  heroSectionMobile: {
    padding: 24,
    paddingTop: 80,
    minHeight: 'auto',
  },
  heroContentMobile: {
    gap: 40,
    maxWidth: '100%',
  },
  heroTextContainerMobile: {
    minWidth: '100%',
  },
  heroTitleMobile: {
    fontSize: 36,
    lineHeight: 44,
    marginBottom: 20,
  },
  heroSubtitleMobile: {
    fontSize: 18,
    lineHeight: 28,
    marginBottom: 32,
  },
  heroButtonsMobile: {
    flexDirection: 'column',
    width: '100%',
    gap: 16,
  },
  primaryButtonMobile: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    width: '100%',
  },
  primaryButtonTextMobile: {
    fontSize: 16,
  },
  secondaryButtonMobile: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    width: '100%',
  },
  secondaryButtonTextMobile: {
    fontSize: 16,
  },
  heroImageContainerMobile: {
    minWidth: '100%',
    minHeight: 400,
  },
  glassCardMobile: {
    padding: 24,
    borderRadius: 20,
  },
  featuresSectionMobile: {
    padding: 40,
    paddingTop: 60,
    paddingBottom: 60,
  },
  sectionTitleMobile: {
    fontSize: 32,
    marginBottom: 40,
  },
  featuresGridMobile: {
    gap: 24,
    maxWidth: '100%',
  },
  featureCardMobile: {
    minWidth: '100%',
    maxWidth: '100%',
    padding: 32,
    borderRadius: 20,
  },
  featureIconMobile: {
    width: 56,
    height: 56,
    borderRadius: 14,
    marginBottom: 20,
  },
  featureIconTextMobile: {
    fontSize: 28,
  },
  featureTitleMobile: {
    fontSize: 22,
    marginBottom: 12,
  },
  featureDescriptionMobile: {
    fontSize: 16,
    lineHeight: 24,
  },
  ctaSectionMobile: {
    padding: 40,
    paddingTop: 60,
    paddingBottom: 60,
  },
  ctaCardMobile: {
    maxWidth: '100%',
  },
  ctaTitleMobile: {
    fontSize: 32,
    marginBottom: 20,
  },
  ctaSubtitleMobile: {
    fontSize: 18,
    marginBottom: 32,
  },
  ctaButtonMobile: {
    paddingVertical: 18,
    paddingHorizontal: 40,
    width: '100%',
  },
  ctaButtonTextMobile: {
    fontSize: 18,
  },

  // Small Desktop Styles (1024px - 1440px)
  heroSectionSmall: {
    padding: 60,
    paddingTop: 100,
  },
  heroContentSmall: {
    maxWidth: 1200,
    gap: 50,
  },
  heroTitleSmall: {
    fontSize: 52,
    lineHeight: 64,
    marginBottom: 24,
  },

  // Large Desktop Styles (1440px+)
  heroSectionLarge: {
    padding: 120,
    paddingTop: 160,
  },
  heroTitleLarge: {
    fontSize: 76,
    lineHeight: 88,
    marginBottom: 36,
  },

  // Landing Page Mobile Header Styles
  headerContainerMobileLanding: {
    borderRadius: 0,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  headerMobileLanding: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  logoButtonMobileLanding: {
    padding: 2,
  },
  logoMobileLanding: {
    width: 140,
    height: 40,
  },
  hamburgerLanding: {
    width: 44,
    height: 44,
    borderRadius: 12,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    padding: 10,
  },
  hamburgerLineLanding: {
    width: 24,
    height: 2,
    borderRadius: 2,
  },
  mobileMenuLanding: {
    position: 'fixed',
    top: 60,
    left: 0,
    right: 0,
    zIndex: 1999,
    borderBottomWidth: 1,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  },
  mobileMenuItemLanding: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  mobileMenuItemTextLanding: {
    fontSize: 16,
    fontWeight: '500',
  },
});

